import { ApplicationEvent } from './../Event/ApplicationEvent';
import { Result } from '@standardnotes/domain-core';
import { ApplicationStage } from '../Application/ApplicationStage';
import { AbstractService } from '../Service/AbstractService';
import { RawStorageKey } from '../Storage/StorageKeys';
import { Platform } from '@standardnotes/models';
export class HomeServerService extends AbstractService {
    constructor(desktopDevice, platform, internalEventBus) {
        super(internalEventBus);
        this.desktopDevice = desktopDevice;
        this.platform = platform;
        this.internalEventBus = internalEventBus;
        this.HOME_SERVER_DATA_DIRECTORY_NAME = '.homeserver';
    }
    async handleEvent(event) {
        if (event.type === ApplicationEvent.ApplicationStageChanged) {
            const stage = event.payload.stage;
            switch (stage) {
                case ApplicationStage.StorageDecrypted_09: {
                    await this.setHomeServerDataLocationOnDevice();
                    break;
                }
                case ApplicationStage.Launched_10: {
                    await this.startHomeServerIfItIsEnabled();
                    break;
                }
            }
        }
    }
    deinit() {
        ;
        this.desktopDevice = undefined;
        super.deinit();
    }
    async getHomeServerStatus() {
        const isHomeServerRunning = await this.desktopDevice.isHomeServerRunning();
        if (!isHomeServerRunning) {
            return { status: 'off', errorMessage: await this.desktopDevice.getHomeServerLastErrorMessage() };
        }
        return {
            status: 'on',
            url: await this.getHomeServerUrl(),
        };
    }
    async getHomeServerLogs() {
        return this.desktopDevice.getHomeServerLogs();
    }
    async getHomeServerUrl() {
        return this.desktopDevice.getHomeServerUrl();
    }
    async startHomeServer() {
        return this.desktopDevice.startHomeServer();
    }
    async stopHomeServer() {
        return this.desktopDevice.stopHomeServer();
    }
    async isHomeServerRunning() {
        return this.desktopDevice.isHomeServerRunning();
    }
    async activatePremiumFeatures(username, subscriptionId) {
        const result = await this.desktopDevice.activatePremiumFeatures(username, subscriptionId);
        if (result !== undefined) {
            return Result.fail(result);
        }
        return Result.ok('Premium features activated');
    }
    async setHomeServerConfiguration(config) {
        await this.desktopDevice.setHomeServerConfiguration(JSON.stringify(config));
    }
    async getHomeServerConfiguration() {
        const configurationJSONString = await this.desktopDevice.getHomeServerConfiguration();
        if (!configurationJSONString) {
            return undefined;
        }
        return JSON.parse(configurationJSONString);
    }
    async enableHomeServer() {
        await this.desktopDevice.setRawStorageValue(RawStorageKey.HomeServerEnabled, 'true');
        await this.startHomeServer();
    }
    async isHomeServerEnabled() {
        const value = await this.desktopDevice.getRawStorageValue(RawStorageKey.HomeServerEnabled);
        return value === 'true';
    }
    async getHomeServerDataLocation() {
        return this.desktopDevice.getRawStorageValue(RawStorageKey.HomeServerDataLocation);
    }
    async disableHomeServer() {
        await this.desktopDevice.setRawStorageValue(RawStorageKey.HomeServerEnabled, 'false');
        const result = await this.stopHomeServer();
        if (result !== undefined) {
            return Result.fail(result);
        }
        return Result.ok('Home server disabled');
    }
    async changeHomeServerDataLocation() {
        const oldLocation = await this.getHomeServerDataLocation();
        const newLocation = await this.desktopDevice.presentDirectoryPickerForLocationChangeAndTransferOld(this.HOME_SERVER_DATA_DIRECTORY_NAME, oldLocation);
        if (!newLocation) {
            const lastErrorMessage = await this.desktopDevice.getDirectoryManagerLastErrorMessage();
            return Result.fail(lastErrorMessage !== null && lastErrorMessage !== void 0 ? lastErrorMessage : 'No location selected');
        }
        await this.desktopDevice.setRawStorageValue(RawStorageKey.HomeServerDataLocation, newLocation);
        await this.desktopDevice.setHomeServerDataLocation(newLocation);
        return Result.ok(newLocation);
    }
    async openHomeServerDataLocation() {
        const location = await this.getHomeServerDataLocation();
        if (location) {
            void this.desktopDevice.openLocation(location);
        }
    }
    async startHomeServerIfItIsEnabled() {
        const homeServerIsEnabled = await this.isHomeServerEnabled();
        if (homeServerIsEnabled) {
            await this.startHomeServer();
        }
    }
    async setHomeServerDataLocationOnDevice() {
        let location = await this.getHomeServerDataLocation();
        if (!location) {
            const documentsDirectory = await this.desktopDevice.getUserDocumentsDirectory();
            if (!documentsDirectory) {
                return;
            }
            const separator = this.platform === Platform.WindowsDesktop ? '\\' : '/';
            location = `${documentsDirectory}${separator}${this.HOME_SERVER_DATA_DIRECTORY_NAME}`;
        }
        await this.desktopDevice.setRawStorageValue(RawStorageKey.HomeServerDataLocation, location);
        await this.desktopDevice.setHomeServerDataLocation(location);
    }
}
